import {
  MIN_FINDING_DAYS,
  rankSnapshotFindings,
  SNAPSHOT_FINDING_SECTIONS,
  type SnapshotFinding,
  snapshotFindingCarriedSay,
  splitSnapshotFindings,
} from "@healthos/shared";
import { CaretDown, CaretUp, Check, Plus, Pulse, Target } from "@phosphor-icons/react";
import { useState } from "react";

/** A dip inside a good run is neither a win to tick nor a problem to flag. */
function whenState(now: NonNullable<SnapshotFinding["now"]>): string {
  if (now.applies) return " is-on";
  return now.dipped ? " is-dip" : " is-holding";
}

function Finding({ finding, carried }: { finding: SnapshotFinding; carried: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="snap-fi">
      {/* The thing to do reads first and largest. It is the answer a member
          came for, and it sat last in the card, under the claim, both arms and
          the device list, in the smallest type on the page.
          Above it, where the member stands right now: the move says what, and
          this says whether today is one of the days it is for. */}
      {finding.now ? (
        <p className={`snap-fi-when${whenState(finding.now)}`}>
          {finding.now.applies ? (
            <Target size={13} weight="bold" aria-hidden="true" />
          ) : finding.now.dipped ? null : (
            <Check size={13} weight="bold" aria-hidden="true" />
          )}
          {finding.now.label}
          {finding.now.dipped
            ? `, ${finding.now.worseIsHigher ? "heavier" : "quieter"} than your last ${finding.now.days} days`
            : ""}
        </p>
      ) : null}
      <p className="snap-fi-move">{finding.move}</p>
      {/* The tie made visible: this measurement is one of the things ordering
          what Heartbeat offers, so the member can see why a moment arrives. */}
      {carried ? (
        <p className="snap-fi-carried">
          <Pulse size={12} weight="bold" aria-hidden="true" />
          {snapshotFindingCarriedSay(finding)}
        </p>
      ) : null}
      <div className="snap-fi-top">
        <span className="snap-fi-claim">{finding.claim}</span>
        <span className="snap-fi-diff">{finding.difference}</span>
      </div>
      {/* The control sits level with the last arm rather than on a row of its
          own. Which devices measured it is part of how sure the comparison is,
          so that moved behind the same toggle. */}
      <div className="snap-fi-body">
        <div className="snap-fi-arms">
          {[finding.low, finding.high].map((arm) => (
            <div key={arm.label} className="snap-fi-arm">
              <span className="snap-fi-k">{arm.label}</span>
              <span className="snap-fi-v">{arm.value}</span>
              <span className="snap-fi-n">{arm.days} days</span>
            </div>
          ))}
        </div>
        <button
          className="snap-fi-sure"
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Hide the numbers" : "How sure is this"}
        </button>
      </div>
      {open && finding.needs.length > 0 ? (
        <p className="snap-fi-needs">
          Measured with{" "}
          {finding.needs.map((n, i) => (
            <span key={n}>
              {i > 0 ? <Plus size={12} aria-hidden="true" /> : null}
              <b>{n}</b>
            </span>
          ))}
        </p>
      ) : null}
      {open ? (
        <dl className="snap-fi-stats">
          {finding.interval ? (
            <div>
              <dt>Interval</dt>
              <dd>{finding.interval}</dd>
            </div>
          ) : null}
          <div>
            <dt>Days</dt>
            <dd>{finding.days}</dd>
          </div>
          <div>
            <dt>Correlation</dt>
            <dd>r = {finding.r.toFixed(2)}</dd>
          </div>
          {finding.reads.length > 0 ? (
            <div>
              <dt>Read from</dt>
              <dd>{finding.reads.join(", ")}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {open ? <p className="snap-fi-note">{finding.note}</p> : null}
    </li>
  );
}

/** One half of the split, with its own collapse. */
function FindingGroup({
  heading,
  hint,
  findings,
  carried,
}: {
  heading: string;
  hint: string;
  findings: ReadonlyArray<SnapshotFinding>;
  /** Whether these are the ones Heartbeat weighs when it picks. */
  carried: boolean;
}) {
  const [all, setAll] = useState(false);
  if (findings.length === 0) return null;
  const ranked = rankSnapshotFindings(findings);
  const shown = all ? ranked : ranked.slice(0, 1);
  const rest = ranked.length - shown.length;
  return (
    <>
      <div className="snap-section-head">
        <h2>{heading}</h2>
        <span className="hint">{hint}</span>
      </div>
      <ul className="snap-fi-list">
        {shown.map((f) => (
          <Finding key={f.id} finding={f} carried={carried} />
        ))}
      </ul>
      {ranked.length > 1 ? (
        <button
          className="snap-fi-more"
          type="button"
          aria-expanded={all}
          onClick={() => setAll((v) => !v)}
        >
          {all ? "Show the biggest only" : `Show ${rest} more`}
          {all ? (
            <CaretUp size={12} weight="bold" aria-hidden="true" />
          ) : (
            <CaretDown size={12} weight="bold" aria-hidden="true" />
          )}
        </button>
      ) : null}
    </>
  );
}

export function SnapshotFindings({
  findings,
  minDays = MIN_FINDING_DAYS,
  pending = false,
  foundElsewhere = false,
  showGoingWell = true,
}: {
  findings: ReadonlyArray<SnapshotFinding>;
  /** Days both readings must share before a comparison can be made. */
  minDays?: number;
  /** True while the readings are still arriving: an empty list is not yet a fact. */
  pending?: boolean;
  /**
   * Findings exist, just not for this tab. Only a handful are kept across the
   * whole page, so a tab can be empty while the member has plenty of paired
   * days, and telling them a comparison needs more days would be untrue.
   */
  foundElsewhere?: boolean;
  /** Whether what is already holding is worth the room. */
  showGoingWell?: boolean;
}) {
  if (pending && findings.length === 0) return null;
  const split = splitSnapshotFindings(findings);
  const improve = split.improve;
  const going = showGoingWell ? split.going : [];
  return (
    <section className="snap-cross">
      {improve.length === 0 && going.length === 0 ? (
        <>
          <div className="snap-section-head">
            <h2>Areas to improve</h2>
            <span className="hint">From your own days</span>
          </div>
          <p className="snap-fi-empty">
            {findings.length > 0 && split.improve.length === 0 && split.going.length === 0
              ? // Comparisons exist for this reading, they simply have no
                // current standing to file under. Telling a member with months
                // of paired days that a comparison needs more days is false.
                "Nothing to act on here yet. These comparisons need a current reading before they can say which side of them you are on."
              : split.going.length > 0
                ? // Comparisons were made for this reading and every one of them
                  // came back fine.
                  "Nothing to improve here. Every comparison for this reading is going well, and they are listed on the Overview."
                : foundElsewhere
                  ? "Nothing here yet. The strongest patterns from your days are on the other tabs."
                  : `Nothing to compare yet. A comparison needs ${minDays} days where two of your sources both reported, and it appears here on its own once they have.`}
          </p>
        </>
      ) : (
        <>
          <FindingGroup
            heading={SNAPSHOT_FINDING_SECTIONS.improve.heading}
            hint={SNAPSHOT_FINDING_SECTIONS.improve.hint}
            findings={improve}
            carried
          />
          <FindingGroup
            heading={SNAPSHOT_FINDING_SECTIONS.going.heading}
            hint={SNAPSHOT_FINDING_SECTIONS.going.hint}
            findings={going}
            carried={false}
          />
        </>
      )}
    </section>
  );
}
